import React, { useState } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FaBox,
  FaIndustry,
  FaShoppingCart,
  FaTools,
  FaFlask,
  FaBoxOpen,
  FaFileContract,
  FaHistory,
  FaBars,
  FaTimes,
  FaSun,
  FaMoon,
  FaSignOutAlt,
  FaPlay,
  FaPause,
  FaCheck,
  FaExclamationTriangle,
} from 'react-icons/fa';
import ProductList from './ProductList';
import ProductContracts from './ProductContracts';
import ContractStages from './ContractStages';
import ContractDetail from './ContractDetail';
import ContractsList from './ContractsList';
import HistoryPage from './HistoryPage';
import DetailsByStage from './DetailsByStage'; // новый компонент
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../auth/AuthContext';
import { ROLE_LABELS, STAGE_GROUPS } from '../auth/roles';
import FallingBackground from './FallingBackground';
import RemnantsPage from './RemnantsPage';
import { FaArchive } from 'react-icons/fa';

function AppShell() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme, bgAnimation, toggleBgAnimation } = useTheme();
  const { user, logout } = useAuth();

  const closeMenu = () => setMenuOpen(false);

  const mainItems = [
    { name: 'Изделия', path: '/', icon: FaBox },
    { name: 'Договоры', path: '/contracts', icon: FaFileContract },
    { name: 'Архив остатков', path: '/remnants', icon: FaArchive },
  ];
  const historyItem = { name: 'История', path: '/history', icon: FaHistory };
  const stageItems = [
    { name: STAGE_GROUPS.production?.label || 'Производство', path: '/production', icon: FaIndustry, group: 'production' },
    { name: STAGE_GROUPS.procurement?.label || 'Закуп', path: '/procurement', icon: FaShoppingCart, group: 'procurement' },
    { name: STAGE_GROUPS.assembly?.label || 'Сборка', path: '/assembly', icon: FaTools, group: 'assembly' },
    { name: STAGE_GROUPS.testing?.label || 'Испытания', path: '/testing', icon: FaFlask, group: 'testing' },
    { name: STAGE_GROUPS.packaging?.label || 'Упаковка', path: '/packaging', icon: FaBoxOpen, group: 'packaging' },
    { name: STAGE_GROUPS.completed?.label || 'Завершено', path: '/completed', icon: FaCheck, group: 'completed' },
    { name: STAGE_GROUPS.cancelled?.label || 'Отменено', path: '/cancelled', icon: FaTimes, group: 'cancelled' },
    { name: STAGE_GROUPS.incorrect?.label || 'Некорректно', path: '/incorrect', icon: FaExclamationTriangle, group: 'incorrect' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const roleLabel = ROLE_LABELS[user?.role] || user?.role;

  return (
    <div className="app-layout">
      {bgAnimation && <FallingBackground />}
      {menuOpen && <div className="app-overlay" onClick={closeMenu} aria-hidden="true" />}

      <aside className={`app-sidebar ${menuOpen ? 'open' : ''}`}>
        <div className="app-sidebar-header">
          <div className="app-sidebar-brand">
            <FaIndustry style={{ color: 'var(--brand)' }} />
            Производство
          </div>
          <button type="button" className="theme-toggle-btn d-lg-none" onClick={closeMenu} aria-label="Закрыть меню">
            <FaTimes />
          </button>
        </div>

        <nav className="app-sidebar-nav">
          <div className="app-sidebar-section">Обзор</div>
          {mainItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`app-nav-item ${isActive(item.path) ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <item.icon />
              {item.name}
            </Link>
          ))}
          {(user?.role === 'admin' || user?.role === 'manager') && (
            <Link
              to={historyItem.path}
              className={`app-nav-item ${isActive(historyItem.path) ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <historyItem.icon />
              {historyItem.name}
            </Link>
          )}
          <div className="app-sidebar-section">Стадии производства</div>
          {stageItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`app-nav-item ${isActive(item.path) ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <item.icon />
              {item.name}
            </Link>
          ))}
        </nav>

        <div className="app-sidebar-footer">
          {user?.username && (
            <div>
              <strong>{user.username}</strong>
              {roleLabel && <div>{roleLabel}</div>}
            </div>
          )}
        </div>
      </aside>

      <div className="app-main">
        <header className="app-header">
          <button
            type="button"
            className="theme-toggle-btn sidebar-toggle-mobile"
            onClick={() => setMenuOpen(true)}
            aria-label="Открыть меню"
          >
            <FaBars />
          </button>
          <span className="app-header-title d-none d-sm-inline">Система управления производством</span>

          <div className="app-header-actions">
            <button
              className="theme-toggle-btn"
              onClick={toggleBgAnimation}
              title={bgAnimation ? 'Выключить анимированный фон' : 'Включить анимированный фон'}
            >
              {bgAnimation ? <FaPlay style={{ color: 'var(--brand)' }} /> : <FaPause />}
            </button>
            <button type="button" className="theme-toggle-btn" onClick={toggleTheme} aria-label="Тема">
              {theme === 'light' ? <FaMoon /> : <FaSun />}
            </button>
            <button type="button" className="theme-toggle-btn" onClick={handleLogout} aria-label="Выход">
              <FaSignOutAlt />
            </button>
          </div>
        </header>

        <main className="app-content">
          <Routes>
            <Route path="/" element={<ProductList />} />
            <Route path="/contracts" element={<ContractsList />} />
            <Route path="/remnants" element={<RemnantsPage />} />
            <Route path="/history" element={<HistoryPage />} />
            <Route path="/product/:productId/contracts" element={<ProductContracts />} />
            <Route path="/contract/:contractId/stages" element={<ContractStages />} />
            <Route path="/contract/:id" element={<ContractDetail />} />
            
            {/* Страницы стадий – показывают детали */}
            <Route path="/production" element={<DetailsByStage stageGroup="production" />} />
            <Route path="/procurement" element={<DetailsByStage stageGroup="procurement" />} />
            <Route path="/assembly" element={<DetailsByStage stageGroup="assembly" />} />
            <Route path="/testing" element={<DetailsByStage stageGroup="testing" />} />
            <Route path="/packaging" element={<DetailsByStage stageGroup="packaging" />} />
            <Route path="/completed" element={<DetailsByStage stageGroup="completed" />} />
            <Route path="/cancelled" element={<DetailsByStage stageGroup="cancelled" />} />
            <Route path="/incorrect" element={<DetailsByStage stageGroup="incorrect" />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

export default AppShell;